import React, { useState, useEffect } from "react"
import {CheckCircle, Clock, XCircle, MessageSquare, User, Calendar, X} from 'lucide-react'
import { lumi } from "../lib/lumi"

interface Task {
  _id: string
  elementType: string
  section: string
  contentKey: string
  currentValue: string
  feedback: string
  status: "pending" | "in_progress" | "completed" | "rejected"
  createdBy: string
  createdByName: string
  createdAt: string
  updatedAt: string
  assignedTo?: string
  completedBy?: string
}

export function TaskManager() {
  const [tasks, setTasks] = useState<Task[]>([])
  const [filter, setFilter] = useState<"all" | "pending" | "in_progress" | "completed">("pending")
  const [loading, setLoading] = useState(true)
  const [isOpen, setIsOpen] = useState(true)

  const user = lumi.auth.user
  const isEditor = user?.userRole === "ADMIN"

  useEffect(() => {
    if (isEditor) {
      fetchTasks()
    }
  }, [filter, isEditor])

  const fetchTasks = async () => {
    try {
      const filterQuery = filter === "all" ? {} : { status: filter }
      const { list } = await lumi.entities.tasks.list({
        filter: filterQuery,
        sort: { createdAt: -1 },
        limit: 50
      })
      setTasks(list)
    } catch (error) {
      console.error("Failed to fetch tasks:", error)
    } finally {
      setLoading(false)
    }
  }

  const updateTaskStatus = async (taskId: string, status: "in_progress" | "completed" | "rejected") => {
    try {
      await lumi.entities.tasks.update(taskId, {
        status,
        completedBy: status === "completed" ? user?.userId : undefined,
        updatedAt: new Date().toISOString()
      })
      await fetchTasks()
    } catch (error) {
      console.error("Failed to update task:", error)
      alert("Échec de la mise à jour. Veuillez réessayer.")
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending": return "bg-yellow-100 text-yellow-800"
      case "in_progress": return "bg-blue-100 text-blue-800"
      case "completed": return "bg-green-100 text-green-800"
      case "rejected": return "bg-red-100 text-red-800"
      default: return "bg-gray-100 text-gray-800"
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending": return "En attente"
      case "in_progress": return "En cours"
      case "completed": return "Terminé"
      case "rejected": return "Rejeté"
      default: return status
    }
  }

  if (!isEditor) return null
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed top-20 right-6 bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-4 py-3 rounded-full shadow-2xl hover:scale-110 transition-transform z-40 flex items-center gap-2"
      >
        <MessageSquare className="w-5 h-5" />
        Tâches ({tasks.length})
      </button>
    )
  }

  return (
    <div className="fixed top-20 right-6 w-96 max-h-[600px] bg-white rounded-2xl shadow-2xl border-2 border-teal-600 overflow-hidden flex flex-col z-40">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xl font-bold flex items-center gap-2">
            <MessageSquare className="w-6 h-6" />
            Tâches de Relecture
          </h3>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 hover:bg-white/20 rounded-lg transition-colors"
            title="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-teal-100">Gérez les feedbacks des relecteurs</p>
      </div>

      {/* Filters */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFilter("all")}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              filter === "all" ? "bg-teal-600 text-white" : "bg-white text-gray-700 hover:bg-gray-100"
            }`}
          >
            Toutes
          </button>
          <button
            onClick={() => setFilter("pending")}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              filter === "pending" ? "bg-yellow-600 text-white" : "bg-white text-gray-700 hover:bg-gray-100"
            }`}
          >
            En attente
          </button>
          <button
            onClick={() => setFilter("in_progress")}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              filter === "in_progress" ? "bg-blue-600 text-white" : "bg-white text-gray-700 hover:bg-gray-100"
            }`}
          >
            En cours
          </button>
          <button
            onClick={() => setFilter("completed")}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              filter === "completed" ? "bg-green-600 text-white" : "bg-white text-gray-700 hover:bg-gray-100"
            }`}
          >
            Terminées
          </button>
        </div>
      </div>

      {/* Tasks List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Chargement...</div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>Aucune tâche</p>
          </div>
        ) : (
          tasks.map((task) => (
            <div key={task._id} className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow">
              {/* Status Badge */}
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${getStatusColor(task.status)}`}>
                  {getStatusLabel(task.status)}
                </span>
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(task.createdAt).toLocaleDateString()}
                </span>
              </div>

              {/* Element Info */}
              <div className="mb-2">
                <div className="text-xs text-gray-500 mb-1">
                  {task.section} → {task.contentKey}
                </div>
                <div className="text-sm font-medium text-gray-900 bg-white p-2 rounded border border-gray-200">
                  {task.currentValue}
                </div>
              </div>

              {/* Feedback */}
              <div className="bg-white p-2 rounded border border-teal-200 mb-2">
                <div className="text-xs text-teal-600 font-semibold mb-1 flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {task.createdByName}
                </div>
                <p className="text-sm text-gray-700">{task.feedback}</p>
              </div>

              {/* Actions */}
              {task.status === "pending" && (
                <div className="flex gap-2">
                  <button
                    onClick={() => updateTaskStatus(task._id, "in_progress")}
                    className="flex-1 flex items-center justify-center gap-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors"
                  >
                    <Clock className="w-3 h-3" />
                    En cours
                  </button>
                  <button
                    onClick={() => updateTaskStatus(task._id, "completed")}
                    className="flex-1 flex items-center justify-center gap-1 bg-green-600 text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-green-700 transition-colors"
                  >
                    <CheckCircle className="w-3 h-3" />
                    Terminé
                  </button>
                  <button
                    onClick={() => updateTaskStatus(task._id, "rejected")}
                    className="flex items-center justify-center bg-red-600 text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-red-700 transition-colors"
                  >
                    <XCircle className="w-3 h-3" />
                  </button>
                </div>
              )}

              {task.status === "in_progress" && (
                <div className="flex gap-2">
                  <button
                    onClick={() => updateTaskStatus(task._id, "completed")}
                    className="flex-1 flex items-center justify-center gap-1 bg-green-600 text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-green-700 transition-colors"
                  >
                    <CheckCircle className="w-3 h-3" />
                    Marquer terminé
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}